import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, Copy, Users, Clock, Sparkles } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { InviteForm, Leaderboard } from '../components/referrals/ReferralWidgets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/referrals`;
const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
const STATUS = {
  invited: ['Invited', 'bg-stone-100 text-stone-600'],
  signed_up: ['Signed up', 'bg-sky-100 text-sky-800'],
  reward_earned: ['Paid · reward earned', 'bg-emerald-100 text-emerald-800'],
  reward_capped: ['Paid · reward capped', 'bg-amber-100 text-amber-800'],
};

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-xl bg-white/5 px-4 py-3 ring-1 ring-white/10">
    <p className="flex items-center gap-1.5 text-xs text-stone-400"><Icon className="h-3.5 w-3.5" /> {label}</p>
    <p className="mt-1 font-mono text-2xl font-bold tabular-nums text-white" data-testid={testId}>{value}</p>
  </div>
);

const History = ({ rows }) => (
  <section className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="referral-history">
    <h2 className="text-base font-semibold text-stone-900">Your invites</h2>
    {rows.length === 0 ? (
      <p className="mt-3 text-sm text-stone-500" data-testid="referral-history-empty">No invites yet. Send your first one above.</p>
    ) : (
      <ul className="mt-3 divide-y divide-stone-100">
        {rows.map(r => (
          <li key={r.id} className="flex flex-col gap-1.5 py-3 text-sm sm:flex-row sm:items-center sm:gap-3" data-testid={`referral-history-row-${r.id}`}>
            <span className="min-w-0 break-all font-medium text-stone-800 sm:flex-1 sm:truncate">{r.friend}</span>
            <span className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-stone-500">
              {r.status === 'invited' ? `Invited ${fmt(r.date)}` : r.completed_at ? `Joined ${fmt(r.date)} · paid ${fmt(r.completed_at)}` : `Joined ${fmt(r.date)}`}
            </span>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS[r.status][1]}`} data-testid={`referral-history-status-${r.id}`}>{STATUS[r.status][0]}</span>
            </span>
          </li>
        ))}
      </ul>
    )}
  </section>
);

export default function Referrals() {
  const [me, setMe] = useState(null);
  const [rows, setRows] = useState([]);
  const [invitesSent, setInvitesSent] = useState(0);
  const [board, setBoard] = useState(null);

  const load = useCallback(() => {
    const opts = { withCredentials: true };
    axios.get(`${API}/me`, opts).then(r => setMe(r.data)).catch(() => toast.error('Could not load referrals'));
    axios.get(`${API}/history`, opts).then(r => { setRows(r.data.rows); setInvitesSent(r.data.invites_sent || 0); }).catch(() => {});
    axios.get(`${API}/leaderboard`, opts).then(r => setBoard(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(me.link); toast.success('Referral link copied'); }
    catch { toast.error('Could not copy — select the link and copy it manually'); }
  };

  const toggleHidden = async (hidden) => {
    try {
      await axios.put(`${API}/leaderboard-visibility`, { hidden }, { withCredentials: true });
      const r = await axios.get(`${API}/leaderboard`, { withCredentials: true });
      setBoard(r.data);
      toast.success(hidden ? "You're hidden from the leaderboard" : "You're visible on the leaderboard");
    } catch { toast.error('Could not update visibility'); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8 pb-16" data-testid="referrals-page">
        <section className="relative overflow-hidden rounded-2xl bg-stone-900 p-6 text-white md:p-8">
          <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />
          <p className="relative inline-flex items-center gap-2 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-medium text-emerald-300"><Gift className="h-3.5 w-3.5" /> Give a month, get a month</p>
          <h1 className="relative mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Referrals</h1>
          <p className="relative mt-3 max-w-2xl text-sm text-stone-400 md:text-base">When a friend signs up with your link and makes their first payment, you both get a free month, up to {me?.max_free_months_per_year ?? 12} a year.</p>
          {me && (
            <>
              <div className="relative mt-6 flex max-w-2xl gap-2">
                <code className="min-w-0 flex-1 truncate rounded-lg bg-white/10 px-3 py-2.5 text-sm text-stone-100 ring-1 ring-white/15" data-testid="referrals-link">{me.link}</code>
                <Button onClick={copy} className="rounded-lg bg-emerald-500 text-stone-950 hover:bg-emerald-400" data-testid="referrals-copy-btn"><Copy className="mr-1.5 h-4 w-4" /> Copy</Button>
              </div>
              <p className="relative mt-2 text-xs text-stone-400">Your code: <span className="font-mono font-semibold text-emerald-300" data-testid="referrals-code">{me.code}</span></p>
              <div className="relative mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
                <Stat icon={Users} label="Friends who paid" value={me.completed_count} testId="referrals-stat-completed" />
                <Stat icon={Clock} label="Signed up, not paid yet" value={me.pending_count} testId="referrals-stat-pending" />
                <Stat icon={Gift} label="Free months this year" value={`${me.free_months_this_year}/${me.max_free_months_per_year}`} testId="referrals-stat-months" />
                <Stat icon={Sparkles} label="Email invites sent" value={invitesSent} testId="referrals-stat-invites" />
              </div>
            </>
          )}
        </section>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
          <div className="space-y-8">
            <section className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
              <h2 className="text-base font-semibold text-stone-900">Invite friends by email</h2>
              <p className="mb-4 mt-1 text-sm text-stone-500">We'll send a branded invite with your link. Replies go to you.</p>
              <InviteForm onSent={load} />
            </section>
            <History rows={rows} />
          </div>
          {board && <div className="lg:self-start"><Leaderboard data={board} onToggleHidden={toggleHidden} /></div>}
        </div>
      </div>
    </DashboardLayout>
  );
}
